import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { useGetRegistrationCountries } from "../../../data/useRegistrationCountries";
import {
  CountryListItem,
  GetRegistrationCountriesResponse,
} from "../../../data/dtos/get-registration-countries.dto";
import { I18nContext } from "../../../contexts/I18nContext";
import { ResponseError } from "../../../data/types/responseError";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

interface CountriesContextType {
  allowedCountries: CountryListItem[];
  forbiddenCountries: string[];
  isLoading: boolean;
  error: ResponseError | null;
  refetch: (
    options?: RefetchOptions,
  ) => Promise<
    QueryObserverResult<GetRegistrationCountriesResponse, ResponseError>
  >;
}

const CountriesContext = createContext<CountriesContextType | undefined>(
  undefined,
);

CountriesContext.displayName = "CountriesContext";

interface CountriesProviderProps {
  children: ReactNode;
}

export const CountriesProvider: React.FC<CountriesProviderProps> = ({
  children,
}) => {
  const { language } = useContext(I18nContext);
  const {
    data: registrationCountries,
    isLoading,
    error,
    refetch,
  } = useGetRegistrationCountries({ language });

  const value: CountriesContextType = useMemo(
    () => ({
      allowedCountries: registrationCountries?.allowedCountries || [],
      forbiddenCountries: registrationCountries?.forbiddenCountries || [],
      isLoading,
      error,
      refetch,
    }),
    [registrationCountries, isLoading, error, refetch],
  );

  return (
    <CountriesContext.Provider value={value}>
      {children}
    </CountriesContext.Provider>
  );
};

export const useCountries = (): CountriesContextType => {
  const context = useContext(CountriesContext);
  if (context === undefined) {
    throw new Error("useCountries must be used within a CountriesProvider");
  }
  return context;
};
